import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type UserRole = Database['public']['Enums']['user_role'];
type Profile = Database['public']['Tables']['profiles']['Row'];
type InviteCode = Database['public']['Tables']['invite_codes']['Row'];
type MemberNote = Database['public']['Tables']['member_notes']['Row'];
type MemberSurvey = Database['public']['Tables']['member_surveys']['Row'];
type OnboardingQuestion = Database['public']['Tables']['onboarding_questions']['Row'];
type OnboardingResponse = Database['public']['Tables']['onboarding_responses']['Row'];

export interface OnboardingAnswer {
  question: string;
  response: string;
  sort_order: number;
}

export interface UserWithRole extends Profile {
  user_roles: { role: UserRole } | null;
  email?: string;
  invited_by?: string | null;
  survey?: MemberSurvey | null;
  onboarding_answers?: OnboardingAnswer[];
}

export function useAdminStats() {
  return useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      // Get counts in parallel
      const [
        { count: activeCount },
        { count: pendingCount },
        { count: appliedCount },
        { count: postsCount },
        { count: eventsCount },
        { count: cohortsCount },
      ] = await Promise.all([
        supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('membership_status', 'approved'),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('membership_status', 'pending'),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('membership_status', 'applied'),
        supabase.from('posts').select('*', { count: 'exact', head: true }),
        supabase.from('events').select('*', { count: 'exact', head: true }),
        supabase.from('cohorts').select('*', { count: 'exact', head: true }),
      ]);

      return {
        activeMembers: activeCount || 0,
        pendingMembers: pendingCount || 0,
        applications: appliedCount || 0,
        posts: postsCount || 0,
        events: eventsCount || 0,
        cohorts: cohortsCount || 0,
      };
    },
  });
}

export function useAdminUsers() {
  return useQuery({
    queryKey: ['admin-users'],
    queryFn: async (): Promise<UserWithRole[]> => {
      // Get all profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profilesError) throw profilesError;
      if (!profiles) return [];

      const userIds = profiles.map((p) => p.user_id);
      const profileIds = profiles.map((p) => p.id);

      // Fetch related data in parallel
      const [
        rolesRes,
        inviteCodesRes,
        surveysRes,
        onboardingQuestionsRes,
        onboardingResponsesRes,
      ] = await Promise.all([
        supabase.from('user_roles').select('user_id, role'),
        supabase
          .from('invite_codes')
          .select('used_by_user_id, created_by_user_id')
          .not('used_by_user_id', 'is', null),
        profileIds.length > 0
          ? supabase.from('member_surveys').select('*').in('profile_id', profileIds)
          : Promise.resolve({ data: [], error: null }),
        supabase
          .from('onboarding_questions')
          .select('*')
          .eq('is_active', true)
          .order('sort_order'),
        userIds.length > 0
          ? supabase.from('onboarding_responses').select('*').in('user_id', userIds)
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (rolesRes.error) throw rolesRes.error;

      // Fetch emails from edge function
      let emailMap: Record<string, string> = {};
      try {
        if (userIds.length > 0) {
          const { data, error } = await supabase.functions.invoke('admin-get-user-emails', {
            body: { user_ids: userIds },
          });
          if (!error && data?.emails) {
            emailMap = data.emails;
          }
        }
      } catch (e) {
        console.error('Failed to fetch user emails:', e);
      }

      const inviteCodes = inviteCodesRes.data || [];

      // Build a map of user_id -> referrer_user_id
      const referrerMap = new Map<string, string>();
      inviteCodes?.forEach((code) => {
        if (code.used_by_user_id && code.created_by_user_id) {
          referrerMap.set(code.used_by_user_id, code.created_by_user_id);
        }
      });

      // Build survey map (keyed by profile_id)
      let surveyMap = new Map<string, MemberSurvey>();
      if (surveysRes.data) {
        surveysRes.data.forEach((s) => {
          if (s.profile_id) surveyMap.set(s.profile_id, s);
        });
      }

      // Fetch all onboarding questions and responses
      let onboardingMap = new Map<string, OnboardingAnswer[]>();
      try {
        const questions = onboardingQuestionsRes.data || [];
        const responses = onboardingResponsesRes.data || [];

        if (questions && responses) {
          const questionMap = new Map(questions.map((q) => [q.id, q]));

          // Group responses by user_id
          responses.forEach((r) => {
            const question = questionMap.get(r.question_id);
            if (!question) return;

            const answer: OnboardingAnswer = {
              question: question.question,
              response: typeof r.response === 'string' ? r.response : JSON.stringify(r.response),
              sort_order: question.sort_order,
            };

            const existing = onboardingMap.get(r.user_id) || [];
            existing.push(answer);
            onboardingMap.set(r.user_id, existing);
          });

          // Sort each user's answers by sort_order
          onboardingMap.forEach((answers, key) => {
            answers.sort((a, b) => a.sort_order - b.sort_order);
          });
        }
      } catch (e) {
        console.error('Failed to fetch onboarding data:', e);
      }

      // Build a map of user_id -> full_name for looking up referrer names
      const nameMap = new Map(profiles.map((p) => [p.user_id, p.full_name]));

      // Merge profiles with roles, emails, invited_by, and survey
      const rolesMap = new Map(rolesRes.data?.map((r) => [r.user_id, r]));

      return profiles.map((profile) => {
        const referrerId = referrerMap.get(profile.user_id);
        return {
          ...profile,
          user_roles: rolesMap.get(profile.user_id) || null,
          email: emailMap[profile.user_id],
          invited_by: referrerId ? nameMap.get(referrerId) || null : null,
          survey: surveyMap.get(profile.id) || null,
          onboarding_answers: onboardingMap.get(profile.user_id) || [],
        };
      });
    },
    staleTime: 1000 * 60,
  });
}

export function useUpdateUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      role,
    }: {
      userId: string;
      role: UserRole;
    }) => {
      // Upsert role
      const { error } = await supabase
        .from('user_roles')
        .upsert(
          {
            user_id: userId,
            role,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id' }
        );

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['user-role'] });
    },
  });
}

export function useRemoveUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['user-role'] });
    },
  });
}

export interface InviteCodeWithReferrals extends InviteCode {
  referrer_name?: string | null;
  invitee_name?: string | null;
}

export function useAdminInviteCodes() {
  return useQuery({
    queryKey: ['admin-invite-codes'],
    queryFn: async (): Promise<InviteCodeWithReferrals[]> => {
      const { data: codes, error } = await supabase
        .from('invite_codes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (!codes) return [];

      // Get unique user IDs for referrers and invitees
      const userIds = new Set<string>();
      codes.forEach((code) => {
        if (code.created_by_user_id) userIds.add(code.created_by_user_id);
        if (code.used_by_user_id) userIds.add(code.used_by_user_id);
      });

      // Fetch profiles for those users
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, full_name')
        .in('user_id', Array.from(userIds));

      const profileMap = new Map(profiles?.map((p) => [p.user_id, p.full_name]) || []);

      // Enrich codes with names
      return codes.map((code) => ({
        ...code,
        referrer_name: code.created_by_user_id ? profileMap.get(code.created_by_user_id) : null,
        invitee_name: code.used_by_user_id ? profileMap.get(code.used_by_user_id) : null,
      }));
    },
  });
}

export function useGenerateInviteCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (createdByUserId: string) => {
      // Generate a random 8-character code
      const code = Math.random().toString(36).substring(2, 10).toUpperCase();

      const { data, error } = await supabase
        .from('invite_codes')
        .insert({
          code,
          created_by_user_id: createdByUserId,
          is_reusable: true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-invite-codes'] });
    },
  });
}

export function useDeleteInviteCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (codeId: string) => {
      const { error } = await supabase
        .from('invite_codes')
        .delete()
        .eq('id', codeId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-invite-codes'] });
    },
  });
}

// Send magic link or password reset via edge function
export function useSendMagicLink() {
  return useMutation({
    mutationFn: async ({
      email,
      userId,
      type,
    }: {
      email?: string;
      userId?: string;
      type?: 'magiclink' | 'recovery' | 'generate_link';
    }) => {
      const { data, error } = await supabase.functions.invoke('send-magic-link', {
        body: {
          email,
          user_id: userId,
          type: type || 'magiclink',
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
  });
}

// Membership management
export function useApproveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase
        .from('profiles')
        .update({ membership_status: 'approved' })
        .eq('user_id', userId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
}

export function useRejectMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase
        .from('profiles')
        .update({ membership_status: 'rejected' })
        .eq('user_id', userId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
}

// Moderation
export function useFlaggedPosts() {
  return useQuery({
    queryKey: ['flagged-posts'],
    queryFn: async () => {
      // For now, just get recently deleted posts for moderation review
      const { data, error } = await supabase
        .from('posts')
        .select('*, profiles!posts_user_id_fkey(*)')
        .eq('is_deleted', true)
        .order('updated_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data || [];
    },
  });
}

export function useModeratePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      postId,
      action,
    }: {
      postId: string;
      action: 'delete' | 'restore';
    }) => {
      const { error } = await supabase
        .from('posts')
        .update({ is_deleted: action === 'delete' })
        .eq('id', postId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['flagged-posts'] });
    },
  });
}

// Member Notes
export interface MemberNoteWithAdmin extends MemberNote {
  admin_name?: string | null;
}

export function useMemberNotes(profileId: string | null) {
  return useQuery({
    queryKey: ['member-notes', profileId],
    queryFn: async (): Promise<MemberNoteWithAdmin[]> => {
      if (!profileId) return [];

      const { data: notes, error } = await supabase
        .from('member_notes')
        .select('*')
        .eq('profile_id', profileId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (!notes || notes.length === 0) return [];

      // Get admin names for the notes
      const adminIds = [...new Set(notes.map((n) => n.admin_id).filter(Boolean))] as string[];
      if (adminIds.length === 0) return notes;

      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, full_name')
        .in('user_id', adminIds);

      const adminNameMap = new Map(profiles?.map((p) => [p.user_id, p.full_name]) || []);

      return notes.map((note) => ({
        ...note,
        admin_name: note.admin_id ? adminNameMap.get(note.admin_id) || null : null,
      }));
    },
    enabled: !!profileId,
  });
}

export function useAddMemberNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      profileId,
      content,
    }: {
      profileId: string;
      content: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('member_notes')
        .insert({
          profile_id: profileId,
          admin_id: user.id,
          content,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, { profileId }) => {
      queryClient.invalidateQueries({ queryKey: ['member-notes', profileId] });
    },
  });
}

export function useDeleteMemberNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      noteId,
      profileId,
    }: {
      noteId: string;
      profileId: string;
    }) => {
      const { error } = await supabase
        .from('member_notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;
    },
    onSuccess: (_, { profileId }) => {
      queryClient.invalidateQueries({ queryKey: ['member-notes', profileId] });
    },
  });
}

// Member Survey
export function useMemberSurvey(profileId: string | null) {
  return useQuery({
    queryKey: ['member-survey', profileId],
    queryFn: async (): Promise<MemberSurvey | null> => {
      if (!profileId) return null;

      const { data, error } = await supabase
        .from('member_surveys')
        .select('*')
        .eq('profile_id', profileId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!profileId,
  });
}
